/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import { isMobile, isTouchDevice } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import CanvaButton from "components/jiffy/react-canva-button/CanvaButton.vue";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import Spinner from "components/jiffy/react-cart/Spinner.vue";
// eslint-disable-next-line import/no-cycle
import ArtworkFullScreen from "./ArtworkFullScreen.vue";
import BackgroundToggle from "./common/BackgroundToggle.vue";
import {
  ARTWORK_IMAGE_MESSAGES,
  DTF001_CATALOG_NUMBER,
  PDP_TYPE_GANG_SHEETS,
  UPLOAD_BUTTON_LABEL
} from "./common/constants";
import ImageCompare from "./common/ImageCompare.vue";
import MobileImageModal from "./common/MobileImageModal.vue";
import SingleImageZoom from "./common/SingleImageZoom.vue";
import DtfProgressBar from "./DtfProgressBar.vue";
import GangSheetBuilderContainer from "./GangSheetBuilderContainer.vue";
import MobileFooterCTA from "./MobileFooterCTA.vue";
import ProcessingProgress from "./ProcessingProgress.vue";
import PuffNotice from "./PuffNotice.vue";
import SpangleNotice from "./SpangleNotice.vue";
import { useSpanglesStore } from "./spangles-module";
import { useDtfStore } from "./store";
import { hasGangSheetError, shouldUseEnhancedMetadata } from "./utils";
import "./ArtworkImage.scss";
import { renderList } from "common/vue/render-list";

export default function ArtworkImage({
  artwork = null,
  withMade = false,
  index = 0,
  isMultiUpload = false,
  fitToContainer = false,
  activeConfigSection = null,
  showFullScreenButton = false,
  overrideBackgroundType = undefined,
  onClick,
  className: restClassName,
  ...restProps
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const inchesInAFoot = 12.0;
  const wrapperMaxHeight = 348.0;
  const canvasWidthOffSet = 3;
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [sparkleGif] = useState(() =>
    remoteImageStaticUrl("gen/spangle-sparkle.gif", { auto: "format" })
  );
  const spanglesLoaderText = "Making spangle magic! Your design is being created.";
  const spanglesEditingText = "Please finish editing the size.";
  const messages = ARTWORK_IMAGE_MESSAGES;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [annotationXOffset, setAnnotationXOffset] = useState(0);
  const [annotationYOffset, setAnnotationYOffset] = useState(0);
  const [downscaledOriginalImage, setDownscaledOriginalImage] = useState(null);
  const dpiGuidelines = "https://www.jiffy.com/artwork-dpi-instructions";
  const [dropzoneContentHeight, setDropzoneContentHeight] = useState(0);
  const store = useDtfStore();
  const spanglesStore = useSpanglesStore();
  const resizeTimeoutRef = useRef(null);

  const artworkRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const previewWrapperRef = useRef(null);
  const dropzoneContainerRef = useRef(null);

  // Store accessors
  function pdpType() {
    return store.pdpType;
  }
  function printAsIs() {
    return store.printAsIs;
  }
  function userType() {
    return store.userType;
  }
  function locale() {
    return store.locale;
  }
  function showModal() {
    return store.showModal;
  }
  function sizeErrorTypes() {
    return store.sizeErrorTypes;
  }
  function catalogNumber() {
    return store.catalogNumber;
  }
  function showArtworkHistoryModal() {
    return store.showArtworkHistoryModal;
  }
  function businessLine() {
    return store.businessLine;
  }
  function isSpangle() {
    return store.isSpangle;
  }
  function isProColor() {
    return store.isProColor;
  }
  function isPuff() {
    return store.isPuff;
  }
  function isSticker() {
    return store.isSticker;
  }
  function isStickerSample() {
    return store.isStickerSample;
  }
  function isJiffyMade() {
    return store.isJiffyMade;
  }
  function pickFromUploadsOrDesigns() {
    return store.pickFromUploadsOrDesigns;
  }
  function puff() {
    return store.puff;
  }
  function artworkBackground() {
    return store.artworkBackground;
  }
  function backgroundType() {
    return store.backgroundType;
  }
  function removeBgLoading() {
    return store.removeBgLoading;
  }
  function isMobileStore() {
    return store.isMobile;
  }
  function sticker() {
    return store.sticker;
  }
  function experiments() {
    return store.experiments;
  }
  function canvaDtfEnabled() {
    return store.canvaDtfEnabled;
  }
  function isStandardArtwork() {
    return store.isStandardArtwork;
  }
  function enableAbAiMonetization() {
    return store.enableAbAiMonetization;
  }
  function showGangSheetBuilder() {
    return store.showGangSheetBuilder;
  }
  function gangSheetBuilder() {
    return store.gangSheetBuilder;
  }
  function showGangSheetBuilderFeature() {
    return store.showGangSheetBuilderFeature;
  }
  function previewCanvasSize() {
    return store.previewCanvasSize;
  }
  function gangSheetCatalogNumbers() {
    return store.gangSheetCatalogNumbers;
  }
  function mobileFooterCTAEnabled() {
    return store.mobileFooterCTAEnabled;
  }
  function isStandardDTFProduct() {
    return store.isStandardDTFProduct;
  }
  function isGoldFoil() {
    return store.isGoldFoil;
  }
  function spangleSizeEditing() {
    return spanglesStore.spangleSizeEditing;
  }
  function isSelectedVersionLoading() {
    return spanglesStore.isSelectedVersionLoading;
  }
  function selectedVersion() {
    return spanglesStore.selectedVersion;
  }
  function originalArtworkUrl() {
    return spanglesStore.originalArtworkUrl;
  }
  function lastSpangledDimensions() {
    return spanglesStore.lastSpangledDimensions;
  }
  function spangleVariations() {
    return spanglesStore.spangleVariations;
  }
  function allAPIsCompleted() {
    return spanglesStore.allAPIsCompleted;
  }

  // Computed
  function isLoading() {
    if (!isPuff()) return false;
    return puff()?.isLoading;
  }

  function puffImage() {
    if (isPuff()) return puff()?.image;
    return null;
  }

  function imageSrc() {
    if (!artwork) return null;
    const tryToFindImageSource = () => {
      if (puffImage()) return puffImage();
      if (artwork.spangled && artwork.spangledImageUrl) return artwork.spangledImageUrl;
      if (artwork.isFile && artwork.file) {
        if (artwork.file?.type === "application/pdf") return null;
        return URL.createObjectURL(artwork.file);
      }
      if (isStandardArtwork()) {
        if (artwork?.enhancementTier === "print_original") return artwork?.originalImageUrl;
        if (artwork?.enhancementCompleted) {
          return artwork?.removeBackground ? artwork?.enhancedNoBgUrl : artwork?.enhancedWithBgUrl;
        }
        return artwork?.originalImageUrl;
      }
      return artwork?.url || artwork?.originalImageUrl;
    };
    return tryToFindImageSource() || artwork?.url;
  }

  function previewImageSrc() {
    return shouldDisableSlider() && !puffImage() ? sliderBeforeImage() : imageSrc();
  }

  function showImagePreview() {
    const baseCondition = withMade ? artwork : artwork && !artwork.isSimulated;
    if (sizeErrorTypes().includes(artwork.errorType?.toString()) && !artwork.uploadError)
      return baseCondition;
    if (showModal()) return baseCondition;
    return baseCondition && !withError() && !artwork.retryFailed;
  }

  function fillAvailable() {
    if (artwork.isFile) return false;
    return imageLoaded;
  }

  function isUserSignedIn() {
    return userType() === "signed_in";
  }

  function metadata() {
    const { removeBackground, metadata: meta, withBgMetadata } = artwork || {};
    if (!withBgMetadata) return meta;
    return removeBackground ? meta : withBgMetadata;
  }

  // eslint-disable-next-line max-lines-per-function
  function arrowWidth() {
    if (artwork?.lastEditedSizeId && artwork?.sizesAndQuantities) {
      const lastEditedSize = artwork.sizesAndQuantities.find(
        s => s.id === artwork.lastEditedSizeId && s.quantity > 0
      );
      if (lastEditedSize) return lastEditedSize.width;
    }
    const activeSizes = artwork?.sizesAndQuantities?.filter(s => s.quantity > 0);
    if (activeSizes?.length > 0) return activeSizes[0].width;
    if (isStandardArtwork()) {
      if (shouldUseEnhancedMetadata(artwork)) {
        if (artwork.removeBackground) {
          return artwork.enhancedMetadata?.widthInch || metadata()?.widthInch || artwork.width;
        }
        return artwork.withBgMetadata?.widthInch || metadata()?.widthInch || artwork.width;
      }
      return artwork?.originalMetadata?.widthInch || metadata()?.widthInch || artwork.width;
    }
    if (previewMetadata()?.widthInch) return previewMetadata().widthInch;
    if (isSet()) return artwork.width;
    return metadata()?.widthInch;
  }

  /* eslint-disable max-lines-per-function */
  function arrowHeight() {
    if (artwork?.lastEditedSizeId && artwork?.sizesAndQuantities) {
      const lastEditedSize = artwork.sizesAndQuantities.find(
        s => s.id === artwork.lastEditedSizeId && s.quantity > 0
      );
      if (lastEditedSize) return lastEditedSize.height;
    }
    const activeSizes = artwork?.sizesAndQuantities?.filter(s => s.quantity > 0);
    if (activeSizes?.length > 0) return activeSizes[0].height;
    if (isStandardArtwork()) {
      if (shouldUseEnhancedMetadata(artwork)) {
        if (artwork.removeBackground) {
          return artwork.enhancedMetadata?.heightInch || metadata()?.heightInch || artwork.height;
        }
        return artwork.withBgMetadata?.heightInch || metadata()?.heightInch || artwork.height;
      }
      return artwork?.originalMetadata?.heightInch || metadata()?.heightInch || artwork.height;
    }
    const previewHeight = previewMetadata()?.heightInch;
    if (previewHeight) return Math.ceil(previewHeight / inchesInAFoot);
    if (isSet()) return artwork.height;
    return metadata().heightInch;
  }

  function isSet() {
    return artwork?.height > 0 && artwork?.width > 0;
  }

  function previewMetadata() {
    return artwork.previewMetadata;
  }

  function isPreviewMatadataPresent() {
    return typeof previewMetadata() !== "object" || Object.keys(previewMetadata()).length === 0;
  }

  function heightUnit() {
    return printAsIs() ? "′" : "";
  }

  function imageMaxHeight() {
    const artworkEl = artworkRef.current;
    const computations = window.getComputedStyle(artworkEl);
    const imageMaxHeightVal = computations.getPropertyValue("max-height");
    return pxToNumber(imageMaxHeightVal);
  }

  function previewWrapperPadding() {
    const previewWrapperEl = previewWrapperRef.current;
    const computations = window.getComputedStyle(previewWrapperEl);
    const leftPadding = computations.getPropertyValue("padding-left");
    return pxToNumber(leftPadding);
  }

  function canvasWidth() {
    if (isPreviewMatadataPresent()) return null;
    /* TODO(vue-migration): this.$refs.artwork?.nodeType — accessing child component DOM node */
    if (!artworkRef.current?.nodeType) return null;
    const previewWrapperEl = previewWrapperRef.current;
    if (previewMetadata().gangSheetRatio < 1) {
      return imageMaxHeight() * previewMetadata().gangSheetRatio + canvasWidthOffSet;
    }
    return previewWrapperEl.clientWidth - previewWrapperPadding();
  }

  function canvasHeight() {
    if (isPreviewMatadataPresent()) return null;
    /* TODO(vue-migration): this.$refs.artwork?.nodeType — accessing child component DOM node */
    if (!artworkRef.current?.nodeType) return null;
    if (previewMetadata().gangSheetRatio < 1) return imageMaxHeight();
    return canvasWidth() / previewMetadata().gangSheetRatio;
  }

  function canvasWidthPx() {
    if (!canvasWidth()) return "auto";
    return `${canvasWidth()}px`;
  }

  function canvasHeightPx() {
    if (canvasHeight() == null) return null;
    return `${canvasHeight()}px`;
  }

  function imageWidth() {
    if (isPreviewMatadataPresent() || previewMetadata()?.cropImage) return "unset";
    return `${canvasWidth() * previewMetadata().widthToGangSheetWidthRatio}px`;
  }

  function imagePreviewHeight() {
    if (canvasHeight() == null) return null;
    return `${canvasHeight() * previewMetadata().heightToGangSheetHeightRatio}px`;
  }

  function imageAlignment() {
    if (isPreviewMatadataPresent()) return "center";
    return "unset";
  }

  function imagePreviewMaxWidth() {
    if (previewMetadata()?.cropImage) return "unset";
    return "100%";
  }

  function withError() {
    return metadata()?.errors?.length > 0 || artwork.errorMessage || artwork.uploadError;
  }

  function hasGangSheetErrorFn() {
    return hasGangSheetError(artwork);
  }

  function pauseSpinner() {
    return artwork.uploadError && artwork.file.type === "application/pdf";
  }

  function getUniqueArtworkId() {
    return `${index}-${artwork.id}-${isMultiUpload ? "multi" : "single"}`;
  }

  function getImageZoomId() {
    return `js-magnify-wrapper-${getUniqueArtworkId()}`;
  }

  function isTappable() {
    return isTouchDevice() || isMobile();
  }

  function zoomText() {
    if (isTappable()) return "";
    return "Hover Image to Zoom";
  }

  function getZoomTextId() {
    return `js-zoom-text-${getUniqueArtworkId()}`;
  }

  function isArtworkLoading() {
    return artwork.isLoading;
  }

  function isArtworkImageWrapperLoading() {
    return isArtworkLoading() || (isStandardArtwork() && !artwork.enhancementCompleted);
  }

  function imageCompareAfterLabel() {
    let label = "Jiffy Print Perfect";
    if (enableAbAiMonetization()) label = "PrintPerfect";
    return label;
  }

  function showSpanglesLoader() {
    return (
      isSpangle() && (spangleSizeEditing() || isSelectedVersionLoading() || isArtworkLoading())
    );
  }

  function showSpanglesComparison() {
    return (
      selectedVersion() &&
      selectedVersion().spangleCsv &&
      !selectedVersion().isLoading &&
      !selectedVersion().error &&
      !isArtworkLoading()
    );
  }

  function showImageComparison() {
    if (imageLoaded && artwork?.success) {
      if (isSpangle()) return showSpanglesComparison();
      if (!isSticker() && isStandardArtwork()) return true;
      return isProColor();
    }
    return false;
  }

  function cmykImage() {
    return imageLoaded && artwork?.removeBackground
      ? artwork?.cmykPreviewImageNoBackgroundUrl
      : artwork?.cmykPreviewImageWithBackgroundUrl;
  }

  function magnifierPosition() {
    return isMultiUpload ? "right" : "left";
  }

  function sliderText() {
    if (artwork?.isLoading) return {};
    if (isProColor()) return { before: "CMYK", after: "ProColor" };
    if (isSpangle()) return { before: "Original", after: "Spangle" };
    return {};
  }

  function printOriginalPreviewImage() {
    if (isGoldFoil()) {
      return artwork?.goldFoilOriginalUrl || artwork?.originalImageUrl;
    }
    return artwork?.originalImageUrl;
  }

  function isArtworkTrimmedWithBackground() {
    return !artwork?.canRemoveBg && artwork?.metadata?.trimmed;
  }

  function sliderBeforeImage() {
    if (activeConfigSection === "print-as-uploaded" && artwork?.originalMime === "image/svg+xml") {
      return artwork?.originalImageUrl;
    }
    if (isProColor() && !isArtworkLoading()) {
      return cmykImage() || downscaledOriginalImage || artwork?.originalImageUrl;
    }
    if (artwork?.enhancementTier === "custom") {
      if (artwork?.originalMime === "image/svg+xml") {
        return artwork?.trimmedWithBgUrl || artwork?.originalImageUrl;
      }
      return artwork?.originalImageUrl;
    }
    if (artwork?.enhancementTier === "print_original") return printOriginalPreviewImage();
    if (showSpanglesComparison()) return originalArtworkUrl();
    if (!isSticker() && isStandardArtwork()) {
      if (
        artwork?.enhancementCompleted &&
        isArtworkTrimmedWithBackground() &&
        artwork?.trimmedWithBgUrl
      ) {
        return artwork?.trimmedWithBgUrl;
      }
      return artwork?.originalImageUrl || imageSrc();
    }
    return imageSrc();
  }

  function isTransparent() {
    return artworkBackground() === "transparent";
  }

  function showZoomButton() {
    return showMagnifier() && !annotateEnabled() && isMobileStore();
  }

  function showMagnifier() {
    if (isStandardArtwork()) return false;
    if (isSpangle()) return imageLoaded && allAPIsCompleted();
    return imageLoaded;
  }

  function messageArray() {
    return isSticker() ? messages.stickers : messages.transfers;
  }

  function currentMessage() {
    return messageArray()[currentIndex];
  }

  function pdpImageWidth() {
    return showImageComparison() ? "auto" : "650px";
  }

  function showFullWidthArtwork() {
    if (fitToContainer) return false;
    if (isStandardArtwork()) return true;
    return isSpangle() && !sticker() && !isMultiUpload;
  }

  function showUploadCloudArea() {
    return sticker() || catalogNumber() === "UVDTF01";
  }

  function showImageProof() {
    return !sticker() && !showUploadCloudArea();
  }

  function shouldShowTShirtBackground() {
    return showImageProof() && pdpType() !== PDP_TYPE_GANG_SHEETS;
  }

  function uploadAreaStyles() {
    return cn(
      "artwork-image__dropzone",
      cloudAndCanvaEnabled() && "artwork-image__dropzone--canva-enabled",
      showUploadCloudArea() && "artwork-image__dropzone--cloud",
      shouldShowTShirtBackground() && "artwork-image__dropzone--t-shirt",
      showGangSheetBuilderFeature() && "artwork-image__dropzone--gang-sheet",
      isStandardArtwork() && "artwork-image__dropzone--standard-artwork"
    );
  }

  function uploadAreaTitle() {
    if (showUploadCloudArea() || showImageProof()) return "Drag your design here";
    return "Get started by dragging your design";
  }

  function uploadAreaPreviousAction() {
    if (showUploadCloudArea() || showImageProof()) return "My Design Library";
    if (pickFromUploadsOrDesigns()) return "Pick from previous uploads or saved designs";
    return "Purchase from your previous uploads";
  }

  function showDropzoneSubtitle() {
    return (
      !isSpangle() && !isPuff() && !showUploadCloudArea() && !isStickerSample() && !showImageProof()
    );
  }

  function showFloatingImageReadyBlock() {
    return showImageProof() && !showImagePreview();
  }

  function showImageReady() {
    // TODO(vue-migration): 'stickers' is not defined in source — likely a bug, always evaluates to true
    return (
      (isArtworkLoading() || showFloatingImageReadyBlock()) &&
      !isSpangle() &&
      !undefined &&
      showImageProof()
    );
  }

  function showImageReadyBlock() {
    return (isArtworkLoading() || showFloatingImageReadyBlock()) && showImageProof();
  }

  function annotateEnabled() {
    return artwork.annotateEnabled;
  }

  function annotatedPoints() {
    return artwork.annotatedPoints;
  }

  function artworkEnhancementProcessing() {
    return isStandardArtwork() && !isArtworkLoading() && !artwork.enhancementCompleted;
  }

  function artworkMetadata() {
    return artwork.metadata;
  }

  function shouldSkipShowArrowShaftCalculation() {
    return (
      !imageLoaded ||
      !artwork?.removeBackground ||
      !artworkMetadata()?.trimCoordinate ||
      artwork?.enhancementTier === "print_original" ||
      artwork?.enhancementTier === "custom" ||
      artworkEnhancementProcessing() ||
      (artwork?.enhancementCompleted && imageSrc() === artwork?.enhancedNoBgUrl)
    );
  }

  function arrowShaftHorizontalLeftPadding() {
    if (
      shouldSkipShowArrowShaftCalculation() ||
      /* TODO(vue-migration): this.$refs.artwork?.imageIntrinsicWidth — accessing child component property */
      !artworkRef.current?.imageIntrinsicWidth ||
      /* TODO(vue-migration): this.$refs.artwork?.$refs?.imageElement — accessing child component ref */
      !artworkRef.current?.$refs?.imageElement
    ) {
      return 0;
    }
    const minLeftOffsetFromAfterImage = Math.min(
      artworkMetadata().trimCoordinate.topLeft[0],
      artworkMetadata().trimCoordinate.bottomLeft[0]
    );
    const leftPadding =
      (artworkRef.current.$refs.imageElement.getBoundingClientRect().width *
        minLeftOffsetFromAfterImage) /
      artworkRef.current.imageIntrinsicWidth;
    return `${leftPadding}px`;
  }

  function arrowShaftHorizontalWidth() {
    if (
      shouldSkipShowArrowShaftCalculation() ||
      /* TODO(vue-migration): this.$refs.artwork?.imageIntrinsicWidth — accessing child component property */
      !artworkRef.current?.imageIntrinsicWidth
    ) {
      return "100%";
    }
    const maxHorizontalOffsetFromAfterImage = Math.max(
      artworkMetadata().trimCoordinate.topRight[0],
      artworkMetadata().trimCoordinate.bottomRight[0]
    );
    /* TODO(vue-migration): this.$refs.artwork.$refs.imageElement — accessing child component ref */
    const horizontalWidth =
      (artworkRef.current.$refs.imageElement.getBoundingClientRect().width *
        maxHorizontalOffsetFromAfterImage) /
      artworkRef.current.imageIntrinsicWidth;
    return `calc(${horizontalWidth}px - ${arrowShaftHorizontalLeftPadding()})`;
  }

  function arrowShaftVerticalTopPadding() {
    if (
      shouldSkipShowArrowShaftCalculation() ||
      /* TODO(vue-migration): this.$refs.artwork?.imageIntrinsicHeight — accessing child component property */
      !artworkRef.current?.imageIntrinsicHeight
    ) {
      return 0;
    }
    const minTopOffsetFromAfterImage = Math.min(
      Math.abs(artworkMetadata().trimCoordinate.topLeft[1]),
      Math.abs(artworkMetadata().trimCoordinate.topRight[1])
    );
    /* TODO(vue-migration): this.$refs.artwork.$refs.imageElement — accessing child component ref */
    const topPadding =
      (artworkRef.current.$refs.imageElement.getBoundingClientRect().height *
        minTopOffsetFromAfterImage) /
      artworkRef.current.imageIntrinsicHeight;
    return `${topPadding}px`;
  }

  function arrowShaftVerticalHeight() {
    if (
      shouldSkipShowArrowShaftCalculation() ||
      /* TODO(vue-migration): this.$refs.artwork?.imageIntrinsicHeight — accessing child component property */
      !artworkRef.current?.imageIntrinsicHeight
    ) {
      return "100%";
    }
    const maxVerticalOffsetFromAfterImage = Math.max(
      Math.abs(artworkMetadata().trimCoordinate.bottomLeft[1]),
      Math.abs(artworkMetadata().trimCoordinate.bottomRight[1])
    );
    /* TODO(vue-migration): this.$refs.artwork.$refs.imageElement — accessing child component ref */
    const verticalHeight =
      (artworkRef.current.$refs.imageElement.getBoundingClientRect().height *
        maxVerticalOffsetFromAfterImage) /
      artworkRef.current.imageIntrinsicHeight;
    return `calc(${verticalHeight}px - ${arrowShaftVerticalTopPadding()})`;
  }

  function uploadButtonTitle() {
    return UPLOAD_BUTTON_LABEL;
  }

  function uploadFromCanvaEnabled() {
    return canvaDtfEnabled() && isUserSignedIn();
  }

  function gangSheetAndCanvaEnabled() {
    return showGangSheetBuilderFeature() && uploadFromCanvaEnabled();
  }

  function cloudAndCanvaEnabled() {
    return showUploadCloudArea() && uploadFromCanvaEnabled();
  }

  function shouldDisableSlider() {
    return artwork.enhancementTier === "print_original" || artwork.enhancementTier === "custom";
  }

  function artworkUploaded() {
    const artworkId = parseInt(artwork?.id, 10);
    if (Number.isNaN(artworkId)) return false;
    return typeof artworkId === "number" && !artwork?.isLoading;
  }

  function showCancelButton() {
    const isEnhancing = artworkUploaded() && artworkEnhancementProcessing();
    return !withError() && isMultiUpload && (isArtworkLoading() || isEnhancing);
  }

  function shouldShowLabels() {
    if (catalogNumber() === DTF001_CATALOG_NUMBER) return true;
    return !isMultiUpload && !withMade;
  }

  function side() {
    if (!isJiffyMade()) return null;
    return index === 0 ? "front" : "back";
  }

  function dynamicTShirtHeight() {
    if (!showImageProof()) {
      if (gangSheetAndCanvaEnabled()) return "330px";
      if (showGangSheetBuilderFeature()) return "280px";
      return "380px";
    }
    const heights = {
      gangSheetWithCanva: { fallback: 360, minimum: 350 },
      gangSheet: { fallback: 320, minimum: 300 },
      standard: { fallback: 400, minimum: 380 }
    };
    let currentVariant;
    if (gangSheetAndCanvaEnabled()) {
      currentVariant = heights.gangSheetWithCanva;
    } else if (showGangSheetBuilderFeature()) {
      currentVariant = heights.gangSheet;
    } else {
      currentVariant = heights.standard;
    }
    if (!dropzoneContentHeight) return `${currentVariant.fallback}px`;
    const calculatedHeight = dropzoneContentHeight + 120; // 80px padding + 40px safety
    return `${Math.max(calculatedHeight, currentVariant.minimum)}px`;
  }

  function smartTShirtPositioning() {
    // Calculate positioning that ensures content fits between neckline and bottom
    if (!showImageProof() || showGangSheetBuilderFeature()) return { top: "50%", left: "50%" };
    // T-shirt anatomy: neckline takes ~15-20% from top
    const necklineSpace = 20; // 20% from top is neckline area
    const bottomMargin = 10; // 10% from bottom for safety margin
    const usableSpace = 100 - necklineSpace - bottomMargin; // 70% usable space
    // Position content in the center of the usable space
    const contentCenterPosition = necklineSpace + usableSpace / 2;
    return {
      top: `${contentCenterPosition}%`,
      // ~55% from top (20% + 35%)
      left: "50%"
    };
  }

  function shouldShowDropzone() {
    return !mobileFooterCTAEnabled() || isSticker() || catalogNumber() === "UVDTF01";
  }

  function arrowShaftHorizontalCompactTop() {
    return isStandardDTFProduct() && !isArtworkLoading() && !artworkEnhancementProcessing()
      ? "-80px"
      : "-15px";
  }

  function backgroundTypeModelGet() {
    return backgroundType();
  }

  function setBackgroundTypeModel(value) {
    store.setBackgroundType(value);
  }

  // Methods
  function setShowArtworkHistoryModal(...args) {
    return store.setShowArtworkHistoryModal(...args);
  }

  function removeArtworkByIndex(...args) {
    return store.removeArtworkByIndex(...args);
  }

  function removeArtwork(...args) {
    return store.removeArtwork(...args);
  }

  function updateArtworkValuesById(...args) {
    return store.updateArtworkValuesById(...args);
  }

  function updateArtworkValues(...args) {
    return store.updateArtworkValues(...args);
  }

  function setBackgroundType(...args) {
    return store.setBackgroundType(...args);
  }

  function setRemovalFile(...args) {
    return store.setRemovalFile(...args);
  }

  function updatePreviewDpi(...args) {
    return store.updatePreviewDpi(...args);
  }

  function downscaleOriginalImage(...args) {
    return store.downscaleOriginalImage(...args);
  }

  function handleCancelProcess(...args) {
    return store.handleCancelProcess(...args);
  }

  function onCanvasLoaded() {
    downscaleOriginalImage({ artworkId: artwork.id });
  }

  function trackAddFileAreaClick() {
    submitDtfEvent("transfer_choose_file_clicked", {
      page: "pdp",
      pdp_type: pdpType(),
      catalog_number: catalogNumber(),
      business_line: businessLine()
    });
  }

  function onDropzoneClick() {
    trackAddFileAreaClick();
    onClick({ replaceArtwork: true });
  }

  function onImageLoad() {
    const newImageLoaded = !artwork.isFile;
    setImageLoaded(newImageLoaded);
    if (!newImageLoaded) return;
    document.querySelector(`.${getZoomTextId()}`)?.classList?.remove("hidden");
  }

  function pxToNumber(pixelString) {
    return Number(pixelString.replace("px", ""));
  }

  function onArtworkHistoryClick() {
    setShowArtworkHistoryModal(!showArtworkHistoryModal());
    submitDtfEvent("previous_purchase_link_clicked", {
      page: "pdp",
      pdp_type: pdpType()
    });
  }

  function onRemoveArtwork() {
    setRemovalFile(artwork.id);
  }

  function toggleComparisonModal() {
    setShowComparisonModal(prev => !prev);
  }

  function randomMessage() {
    setCurrentIndex(Math.floor(Math.random() * messageArray().length));
  }

  function annotateImage(event) {
    if (!artwork.annotateEnabled) return;
    const imageWrapperEl = imageWrapperRef.current;
    /* TODO(vue-migration): this.$refs.artwork.originalWidth/originalHeight — accessing child component properties */
    const { originalWidth, originalHeight } = artworkRef.current;
    const containerRect = imageWrapperEl.getBoundingClientRect();
    const containerAspectRatio = containerRect.width / containerRect.height;
    const imageAspectRatio = originalWidth / originalHeight;
    let [canvasW, canvasH] = [0, 0];
    if (imageAspectRatio > containerAspectRatio) {
      canvasW = containerRect.width;
      canvasH = originalHeight * (canvasW / originalWidth);
    } else {
      canvasH = containerRect.height;
      canvasW = originalWidth * (canvasH / originalHeight);
    }
    const xOffset = (containerRect.width - canvasW) / 2;
    const yOffset = (containerRect.height - canvasH) / 2;
    let { clientX } = event;
    let { clientY } = event;
    if (event.type === "touchstart") {
      const touch = event.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    }
    const x = clientX - containerRect.x - xOffset;
    const y = clientY - containerRect.y - yOffset;
    const points = [...artwork.annotatedPoints];
    if (x < 0 || x > canvasW || y < 0 || y > canvasH) return;
    setAnnotationXOffset(xOffset);
    setAnnotationYOffset(yOffset);
    points.push({ x, y, mode: artwork.annotateMode });
    updateArtworkValuesById({
      id: artwork.id,
      annotatedPoints: points,
      annotatedCanvasWidth: canvasW,
      annotatedCanvasHeight: canvasH
    });
  }

  function measureDropzoneHeight() {
    queueMicrotask(() => {
      setTimeout(() => {
        if (dropzoneContainerRef.current && showImageProof()) {
          const { height } = dropzoneContainerRef.current.getBoundingClientRect();
          if (height > 50) {
            setDropzoneContentHeight(height);
          }
        }
      }, 50);
    });
  }

  function remeasureHeight() {
    queueMicrotask(() => {
      measureDropzoneHeight();
      setTimeout(() => measureDropzoneHeight(), 100);
    });
  }

  function handleResize() {
    // Debounce resize calls
    clearTimeout(resizeTimeoutRef.current);
    resizeTimeoutRef.current = setTimeout(() => {
      measureDropzoneHeight();
    }, 150);
  }

  function onUploadImageClick() {
    trackAddFileAreaClick();
    onClick({ replaceArtwork: true });
  }

  // Watches
  useEffect(() => {
    setImageLoaded(!artwork.isFile);
    setDownscaledOriginalImage(artwork.downscaledOriginalImage);
  }, [artwork]);

  useEffect(() => {
    /* TODO(vue-migration): async callback reads isArtworkLoading, artwork.enhancementCompleted — possible stale closure */
    if (
      !isArtworkLoading() &&
      artwork.enhancementCompleted &&
      previewCanvasSize().width > 0 &&
      previewCanvasSize().height > 0
    ) {
      updatePreviewDpi({ artworkId: artwork.id });
      downscaleOriginalImage({ artworkId: artwork.id });
    }
  }, [JSON.stringify(previewCanvasSize())]);

  useEffect(() => {
    remeasureHeight();
  }, [isUserSignedIn()]);

  useEffect(() => {
    remeasureHeight();
  }, [uploadFromCanvaEnabled()]);

  useEffect(() => {
    remeasureHeight();
  }, [showGangSheetBuilderFeature()]);

  // Mounted / beforeUnmount
  useEffect(() => {
    setSliderPosition(isSpangle() ? 0 : 50);
    setInterval(randomMessage, 10000);
    // Initial measurements to catch DOM updates
    remeasureHeight();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div
        className={cn(
          "artwork-image scope-297dd82d",
          withMade && "artwork-image--with-made",
          shouldShowTShirtBackground() && !showImagePreview() && "artwork-image__t-shirt",
          shouldShowTShirtBackground() &&
            !showImagePreview() &&
            showGangSheetBuilderFeature() &&
            "artwork-image__t-shirt--gang-sheet-upload",
          gangSheetAndCanvaEnabled() &&
            "artwork-image__t-shirt--gang-sheet-upload-with-canva-enabled",
          restClassName
        )}
        {...restProps}
        style={{
          "--297dd82d-dynamicTShirtHeight": dynamicTShirtHeight(),
          "--297dd82d-smartTShirtPositioning-top": smartTShirtPositioning().top,
          "--297dd82d-smartTShirtPositioning-left": smartTShirtPositioning().left,
          "--297dd82d-imagePreviewMaxWidth": imagePreviewMaxWidth(),
          "--297dd82d-imageWidth": imageWidth(),
          "--297dd82d-imagePreviewHeight": imagePreviewHeight(),
          "--297dd82d-pdpImageWidth": pdpImageWidth(),
          "--297dd82d-artworkBackground": artworkBackground(),
          "--297dd82d-canvasWidthPx": canvasWidthPx(),
          "--297dd82d-canvasHeightPx": canvasHeightPx(),
          "--297dd82d-imageAlignment": imageAlignment(),
          "--297dd82d-arrowShaftHorizontalCompactTop": arrowShaftHorizontalCompactTop(),
          "--297dd82d-arrowShaftHorizontalLeftPadding": arrowShaftHorizontalLeftPadding(),
          "--297dd82d-arrowShaftHorizontalWidth": arrowShaftHorizontalWidth(),
          "--297dd82d-arrowShaftVerticalTopPadding": arrowShaftVerticalTopPadding(),
          "--297dd82d-arrowShaftVerticalHeight": arrowShaftVerticalHeight()
        }}
      >
        {showImagePreview() ? (
          <div
            className={cn(
              "artwork-image__preview scope-297dd82d",
              !imageSrc() && "artwork-image__preview--empty",
              withError() && "artwork-image__preview--with-error",
              !artwork.width && "artwork-image__preview--without-width",
              showFullWidthArtwork() && "artwork-image__preview--full-height",
              fitToContainer && "artwork-image__preview--fit-to-container"
            )}
          >
            {!withMade || (withMade && imageSrc()) ? (
              <div
                className={cn(
                  "artwork-image__preview-wrapper scope-297dd82d",
                  !artwork.height && "artwork-image__preview-wrapper--without-height",
                  showFullWidthArtwork() &&
                    artwork.height &&
                    "artwork-image__preview-wrapper--max-width"
                )}
                ref={previewWrapperRef}
              >
                {isStandardDTFProduct() &&
                !isArtworkLoading() &&
                !artworkEnhancementProcessing() ? (
                  <div
                    className={cn(
                      "artwork-info__actions artwork-info__actions--experiment scope-297dd82d",
                      backgroundType() === "checkered" && "artwork-info__actions--bg-checkered",
                      backgroundType() === "green" && "artwork-info__actions--bg-green"
                    )}
                  >
                    {!hasGangSheetErrorFn() ? (
                      <BackgroundToggle
                        className="scope-297dd82d"
                        modelValue={backgroundTypeModelGet()}
                        onUpdateModelValue={val => {
                          setBackgroundTypeModel(val);
                        }}
                        data-testid="background-toggle"
                      />
                    ) : null}
                    <button
                      className="artwork-info__remove-button artwork-info__remove-button--experiment scope-297dd82d"
                      type="button"
                      onClick={onRemoveArtwork}
                      data-testid="artwork-info-remove-button"
                    >
                      <UseSvg
                        className="artwork-info__trash-icon scope-297dd82d"
                        icon="trash"
                        data-testid="artwork-info-trash-icon"
                      />
                    </button>
                  </div>
                ) : null}
                <div
                  className={cn(
                    "artwork-image__preview-image scope-297dd82d",
                    fillAvailable() && "artwork-image__preview-image--loaded",
                    isTransparent() && "artwork-image__preview-image--transparent-background",
                    showFullWidthArtwork() && "artwork-image__preview-image--full-width",
                    isStandardArtwork() && "artwork-image__preview-image--standard-artwork"
                  )}
                >
                  {artwork.processingImage ? (
                    <div className="artwork-image__loader scope-297dd82d">
                      <Spinner
                        className="artwork-image__loader-spinner scope-297dd82d"
                        theme="white"
                        data-testid="artwork-image-loader-spinner"
                      />
                    </div>
                  ) : null}
                  {isStandardArtwork() ? (
                    <>
                      {isArtworkLoading() && !spangleSizeEditing() ? (
                        <div className="artwork-image__loading-wrapper scope-297dd82d">
                          <div className="artwork-image__loading-box-content scope-297dd82d">
                            <UseSvg
                              className="artwork-image__loading-box-content-icon scope-297dd82d mx-auto"
                              icon="cloud-upload-2"
                              data-testid="artwork-image-loading-box-content-icon"
                            />

                            {isArtworkLoading() ? (
                              <DtfProgressBar
                                className="scope-297dd82d"
                                artwork={artwork}
                                index={index}
                                data-testid="dtf-progress-bar"
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {isArtworkLoading() && !spangleSizeEditing() ? (
                        <div className="artwork-image__sparkle-gif-container scope-297dd82d">
                          <img
                            className="artwork-image__sparkle-gif scope-297dd82d"
                            src={sparkleGif}
                            alt="Jiffy Designs"
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                  {showSpanglesLoader() ? (
                    <div
                      className={cn(
                        "artwork-image__preview-overlay-loader scope-297dd82d",
                        spangleSizeEditing() &&
                          "artwork-image__preview-overlay-loader--editing-size"
                      )}
                    >
                      {spangleSizeEditing() ? (
                        <UseSvg
                          className="artwork-image__preview-overlay-loader-icon scope-297dd82d"
                          icon="pencil"
                          data-testid="artwork-image-preview-overlay-loader-icon"
                        />
                      ) : null}
                      {spangleSizeEditing() ? (
                        <span className="artwork-image__loader-text scope-297dd82d">
                          {spanglesEditingText}
                        </span>
                      ) : (
                        <ProcessingProgress
                          className="artwork-image__loader-text scope-297dd82d"
                          message={spanglesLoaderText}
                          data-testid="artwork-image-loader-text"
                        />
                      )}
                      {!spangleSizeEditing() ? (
                        <button
                          className="artwork-image__cancel scope-297dd82d"
                          type="button"
                          onClick={() => {
                            handleCancelProcess(artwork);
                          }}
                          data-testid="artwork-image-cancel"
                        >
                          {" "}
                          Cancel{" "}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {!showImageProof() && !isStandardArtwork() && isArtworkLoading() ? (
                    <div
                      className={cn(
                        "artwork-image__preview-overlay-loader artwork-image__preview-overlay-loader--tips-wrapper scope-297dd82d",
                        showModal() && "artwork-image__preview-overlay-loader--tips-wrapper-modal"
                      )}
                    >
                      <div
                        className={cn(
                          "artwork-image__preview-overlay-loader-jiffy-tips scope-297dd82d",
                          showModal() && "artwork-image__preview-overlay-loader-jiffy-tips--modal"
                        )}
                      >
                        <UseSvg
                          className="artwork-image__preview-overlay-loader-tips-icon scope-297dd82d"
                          icon="lightbulb-info"
                          data-testid="artwork-image-preview-overlay-loader-tips-icon"
                        />

                        <span className="scope-297dd82d">JIFFY TIPS</span>
                      </div>
                      <span
                        className={cn(
                          "artwork-image__loader-text scope-297dd82d",
                          showModal() && "artwork-image__loader-text--modal"
                        )}
                      >
                        {currentMessage()}
                      </span>
                    </div>
                  ) : null}
                  {artwork.width ? (
                    <div
                      className={cn(
                        "artwork-image__arrow-shaft artwork-image__arrow-shaft--horizontal scope-297dd82d",
                        showFullWidthArtwork() && "artwork-image__arrow-shaft--horizontal-compact",
                        isStandardArtwork() && "artwork-image__arrow-shaft--standard-artwork"
                      )}
                    >
                      <div className="artwork-image__dimension scope-297dd82d">{arrowWidth()}"</div>
                      <div className="artwork-image__arrow artwork-image__arrow--left scope-297dd82d" />
                      <div className="artwork-image__arrow artwork-image__arrow--right scope-297dd82d" />
                    </div>
                  ) : null}
                  {artwork.height ? (
                    <div
                      className={cn(
                        "artwork-image__arrow-shaft artwork-image__arrow-shaft--vertical scope-297dd82d",
                        showFullWidthArtwork() && "artwork-image__arrow-shaft--vertical-compact",
                        isStandardArtwork() && "artwork-image__arrow-shaft--standard-artwork",
                        isStandardDTFProduct() && "artwork-image__arrow-shaft--experiment"
                      )}
                    >
                      <div
                        className={cn(
                          "artwork-image__dimension scope-297dd82d",
                          showFullWidthArtwork() && "artwork-image__dimension--vertical"
                        )}
                      >
                        {arrowHeight()}
                        {heightUnit()}
                      </div>
                      <div className="artwork-image__arrow artwork-image__arrow--top scope-297dd82d" />
                      <div className="artwork-image__arrow artwork-image__arrow--bottom scope-297dd82d" />
                    </div>
                  ) : null}
                  <div
                    className={cn(
                      "artwork-image__image-wrapper image-magnifier scope-297dd82d",
                      getImageZoomId(),
                      isArtworkImageWrapperLoading() && "artwork-image__image-wrapper--loading"
                    )}
                    ref={imageWrapperRef}
                    onClick={annotateImage}
                    onTouchStart={annotateImage}
                    data-testid="artwork-image-image-wrapper"
                  >
                    {isLoading() ? (
                      <div className="artwork-image__loader scope-297dd82d">
                        <Spinner
                          className="artwork-image__loader-indicator scope-297dd82d"
                          theme="white"
                          data-testid="artwork-image-loader-indicator"
                        />
                      </div>
                    ) : null}
                    {!artworkEnhancementProcessing() && !isArtworkLoading() ? (
                      <SingleImageZoom
                        className={cn(
                          "scope-297dd82d",
                          "artwork-image__image",
                          showFullWidthArtwork() && "artwork-image__image--full-width",
                          fitToContainer && "artwork-image__image--fit-to-container",
                          isStandardArtwork() && "artwork-image__image--standard-artwork",
                          isStandardDTFProduct() && "artwork-image__image--experiment"
                        )}
                        ref={artworkRef}
                        data-testid="artwork-image-single-image-zoom"
                        src={previewImageSrc()}
                        alt="Preview Image"
                        wrapperClass={getImageZoomId()}
                        isModal={isMultiUpload}
                        position={magnifierPosition()}
                        isArtwork={!(isProColor() || isSpangle() || isSticker())}
                        isFullWidth={showFullWidthArtwork()}
                        imageBackground={artworkBackground()}
                        overrideBackgroundType={overrideBackgroundType}
                        onLoad={onImageLoad}
                        onCanvasLoaded={onCanvasLoaded}
                      />
                    ) : (
                      <ImageCompare
                        className={cn(
                          "scope-297dd82d",
                          "artwork-image__image js-magnify-image",
                          showFullWidthArtwork() && "artwork-image__image--full-width",
                          fitToContainer && "artwork-image__image--fit-to-container",
                          isStandardArtwork() && "artwork-image__image--standard-artwork"
                        )}
                        ref={artworkRef}
                        beforeImage={sliderBeforeImage()}
                        afterImage={previewImageSrc()}
                        magnifierPosition={magnifierPosition()}
                        beforeText={sliderText().before}
                        afterText={sliderText().after}
                        showMagnifier={!isTappable() && showMagnifier()}
                        sliderPosition={sliderPosition}
                        imageBackground={artworkBackground()}
                        isFullWidth={showFullWidthArtwork()}
                        isModal={isMultiUpload}
                        enhancementProcessing={artworkEnhancementProcessing()}
                        isArtwork={true}
                        alt="Preview Image"
                        selectedLabel={
                          activeConfigSection === "jiffy-print-perfect" ? "after" : "before"
                        }
                        enhancementTier={artwork.enhancementTier}
                        onLoad={onImageLoad}
                        onCanvasLoaded={onCanvasLoaded}
                        slots={{
                          beforeText: () => (
                            <>
                              {" "}
                              Before{" "}
                              {shouldShowLabels() ? (
                                <span className="artwork-image__comparison-slider-label scope-297dd82d">
                                  {" "}
                                  ({Math.round(artwork.sizeBasedDpi)} DPI){" "}
                                </span>
                              ) : null}
                            </>
                          ),

                          afterText: () => (
                            <>
                              {imageCompareAfterLabel()}{" "}
                              {shouldShowLabels() ? (
                                <span className="artwork-image__comparison-slider-label scope-297dd82d">
                                  {" "}
                                  ({artwork.enhancedDpi} DPI){" "}
                                </span>
                              ) : null}
                            </>
                          )
                        }}
                        data-testid="image-compare"
                      />
                    )}
                    {renderList(annotatedPoints(), (point, i) => (
                      <div
                        className={cn("scope-297dd82d", `artwork-image__${point.mode}`)}
                        key={i}
                        style={{
                          left: `${(point.x + annotationXOffset - 5).toString()}px`,
                          top: `${(point.y + annotationYOffset - 5).toString()}px`
                        }}
                      />
                    ))}
                  </div>
                  {showZoomButton() ? (
                    <button
                      className={cn(
                        "artwork-image__zoom-button scope-297dd82d",
                        showImageProof() && "artwork-image__zoom-button--t-shirt"
                      )}
                      type="button"
                      onClick={toggleComparisonModal}
                      data-testid="artwork-image-zoom-button"
                    >
                      <UseSvg
                        className="artwork-image__zoom-icon scope-297dd82d"
                        icon="search"
                        data-testid="artwork-image-zoom-icon"
                      />
                    </button>
                  ) : null}
                </div>
                {showMagnifier() ? (
                  <span
                    className={cn(
                      "scope-297dd82d",
                      `artwork-image__zoom-text hidden ${getZoomTextId()}`
                    )}
                  >
                    {zoomText()}
                  </span>
                ) : null}
              </div>
            ) : (
              <Spinner
                className="artwork-image__spinner scope-297dd82d"
                paused={pauseSpinner()}
                data-testid="artwork-image-spinner"
              />
            )}
            {showComparisonModal ? (
              <MobileImageModal
                className="scope-297dd82d"
                data-testid="artwork-image-mobile-image-modal"
                src={previewImageSrc()}
                wrapperClass={"zoom-image-modal"}
                onClose={toggleComparisonModal}
              />
            ) : null}
          </div>
        ) : shouldShowDropzone() ? (
          <div
            className={cn("scope-297dd82d", uploadAreaStyles())}
            ref={dropzoneContainerRef}
            onClick={onDropzoneClick}
            data-testid="artwork-image-div"
          >
            {showUploadCloudArea() ? (
              <div
                className={cn(
                  "artwork-image__cloud scope-297dd82d",
                  uploadFromCanvaEnabled() && "artwork-image__cloud--canva-enabled"
                )}
              >
                <UseSvg
                  className="artwork-image__cloud-box scope-297dd82d"
                  icon="upload-cloud-box"
                  data-testid="artwork-image-cloud-box"
                />

                <UseSvg
                  className="artwork-image__cloud-border scope-297dd82d"
                  icon="upload-cloud-border"
                  data-testid="artwork-image-cloud-border"
                />
              </div>
            ) : null}
            {showGangSheetBuilderFeature() ? (
              <div className="artwork-image__gang-sheet-note scope-297dd82d">
                <UseSvg
                  className="artwork-image__gang-sheet-note-icon scope-297dd82d"
                  icon="lightbulb-info"
                  data-testid="artwork-image-gang-sheet-note-icon"
                />

                <span className="scope-297dd82d">
                  {" "}
                  Gang sheet width should be <b className="scope-297dd82d">
                    22" (6600px)
                  </b> with <b className="scope-297dd82d">300 DPI</b>.{" "}
                  <a
                    className="scope-297dd82d"
                    href={dpiGuidelines}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    data-testid="artwork-image-a"
                  >
                    {" "}
                    View Image Guidelines{" "}
                  </a>
                </span>
              </div>
            ) : null}
            <div className="artwork-image__info hidden-xs scope-297dd82d">
              {isSpangle() && !showImageProof() ? (
                <SpangleNotice className="scope-297dd82d" data-testid="spangle-notice" />
              ) : null}
              {isPuff() && !showImageProof() ? (
                <PuffNotice className="scope-297dd82d" data-testid="puff-notice" />
              ) : !showUploadCloudArea() ? (
                <UseSvg
                  className={cn(
                    "artwork-image__icon scope-297dd82d",
                    isStandardArtwork() && "artwork-image__icon--standard-artwork"
                  )}
                  icon="cloud-upload"
                  data-testid="artwork-image-icon"
                />
              ) : null}
              <p className="artwork-image__title scope-297dd82d">{uploadAreaTitle()}</p>
              {showDropzoneSubtitle() ? (
                <p className="artwork-image__subtitle scope-297dd82d">
                  {" "}
                  Multiple designs? Drag or select all your files together.{" "}
                </p>
              ) : null}
              <p
                className={cn(
                  "artwork-image__divider scope-297dd82d",
                  isStandardArtwork() && "artwork-image__divider--standard-artwork"
                )}
              />
            </div>
            {isSpangle() && !showImageProof() ? (
              <div className="artwork-image__spangle-mobile-notice visible-xs scope-297dd82d">
                <SpangleNotice className="scope-297dd82d" data-testid="spangle-notice-1" />
              </div>
            ) : null}
            {isPuff() && !showImageProof() ? (
              <div className="artwork-image__spangle-mobile-notice visible-xs scope-297dd82d">
                <PuffNotice className="scope-297dd82d" data-testid="puff-notice-1" />
              </div>
            ) : null}
            <div className="artwork-image__action-buttons scope-297dd82d">
              {showGangSheetBuilderFeature() ? (
                <GangSheetBuilderContainer
                  className="scope-297dd82d"
                  data-testid="gang-sheet-builder-container"
                />
              ) : null}
              <div
                className={cn(
                  "artwork-image__action scope-297dd82d",
                  isStandardArtwork() && "artwork-image__action--standard-artwork"
                )}
              >
                {showUploadCloudArea() || showImageProof() ? (
                  <>
                    <span className="artwork-image__action-title scope-297dd82d">
                      {uploadButtonTitle()}
                    </span>
                    <span className="artwork-image__action-title--mobile scope-297dd82d">
                      {uploadButtonTitle()}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="hidden-xs scope-297dd82d">{uploadButtonTitle()}</span>
                    <span className="artwork-image__action-container scope-297dd82d">
                      <UseSvg
                        className={cn(
                          "artwork-image__icon scope-297dd82d",
                          isStandardArtwork() && "artwork-image__icon--standard-artwork"
                        )}
                        icon="cloud-upload"
                        data-testid="artwork-image-icon-1"
                      />{" "}
                      {uploadButtonTitle()}
                    </span>
                  </>
                )}
              </div>
            </div>
            {!mobileFooterCTAEnabled() ? (
              <>
                {isUserSignedIn() ? (
                  <a
                    className={cn(
                      "artwork-image__dropzone-button artwork-image__dropzone-button--purchase scope-297dd82d",
                      isStandardArtwork() && "artwork-image__dropzone-button--standard-artwork"
                    )}
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onArtworkHistoryClick();
                    }}
                    data-testid="artwork-image-dropzone-button"
                  >
                    {uploadAreaPreviousAction()}
                  </a>
                ) : (
                  <span className="artwork-image__sign-in scope-297dd82d">
                    <a
                      className={cn(
                        "artwork-image__link scope-297dd82d",
                        isStandardArtwork() && "artwork-image__link--standard-artwork"
                      )}
                      href="/login"
                      onClick={e => {
                        e.stopPropagation();
                      }}
                      data-testid="artwork-image-link"
                    >
                      {" "}
                      Log in{" "}
                    </a>{" "}
                    to see purchased designs.{" "}
                  </span>
                )}
              </>
            ) : null}
            {uploadFromCanvaEnabled() ? (
              <div
                className={cn(
                  "artwork-image__dropzone-button artwork-image__dropzone-button--purchase artwork-image__dropzone-button--canva scope-297dd82d",
                  isStandardArtwork() && "artwork-image__dropzone-button--standard-artwork"
                )}
                onClick={e => {
                  e.stopPropagation();
                }}
                data-testid="artwork-image-dropzone-button-1"
              >
                <CanvaButton
                  className="scope-297dd82d"
                  fileLanding={"#product-details-dtf-file-input"}
                  namespace={"dtf"}
                  data-testid="canva-button"
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {!isStandardArtwork() && isArtworkLoading() ? (
          <DtfProgressBar
            className="scope-297dd82d"
            artwork={artwork}
            index={index}
            data-testid="dtf-progress-bar-1"
          />
        ) : null}
        {showImagePreview() && showFullScreenButton ? (
          <ArtworkFullScreen
            className="scope-297dd82d"
            withMade={withMade}
            artwork={artwork}
            activeConfigSection={activeConfigSection}
            shouldDisableSlider={shouldDisableSlider()}
            sliderBeforeImage={sliderBeforeImage()}
            data-testid="artwork-full-screen"
          />
        ) : null}
        {showCancelButton() ? (
          <button
            className={cn(
              "scope-297dd82d",
              "artwork-image__cancel",
              "artwork-image__cancel--dtf",
              isStandardArtwork() ? "artwork-image__cancel--dtf-v2" : ""
            )}
            type="button"
            onClick={() => {
              handleCancelProcess(artwork);
            }}
            data-testid="artwork-image-button"
          >
            <UseSvg
              className="artwork-image__cancel-icon scope-297dd82d"
              icon="modal-close"
              data-testid="artwork-image-cancel-icon"
            />{" "}
            Cancel{" "}
          </button>
        ) : null}
      </div>
      {mobileFooterCTAEnabled() && !showImagePreview() ? (
        <MobileFooterCTA
          className="scope-297dd82d"
          isUserSignedIn={isUserSignedIn()}
          canvaDtfEnabled={canvaDtfEnabled()}
          fileLanding={"#product-details-dtf-file-input"}
          namespace="dtf"
          onUploadImageClick={onUploadImageClick}
          onArtworkHistoryClick={onArtworkHistoryClick}
          data-testid="mobile-footer-cta"
        />
      ) : null}
    </>
  );
}
